import { MouseEvent, useEffect, useState } from "react";
import moment, { Moment } from "moment";
import {
  Box,
  Chip,
  Dialog,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
} from "@mui/material";
import { visuallyHidden } from "@mui/utils";
import { HistoryOutlined } from "@mui/icons-material";

import conf from "config-dlm";
import Refresh from "components/utils/Refresh";
import ErrorChip from "components/utils/ErrorChip";
import JsonModal from "components/utils/Json";
import usePagination from "hooks/pagination";
import EmptyTableRow from "components/rows/Empty";
import SectionHeader from "components/utils/SectionHeader";
import LinearIndeterminate from "components/utils/LinearLoader";
import { useSmartContent } from "components/utils/SmartCell";
import { HistorySchema, usePavmHistory } from "hooks/pavm/history";

const HistoryView = (props: any) => {
  const { onClose, selectedValue, open, config } = props;
  const { host, serialNumber } = config;

  // states to manage date range filters (from -> to)
  const [dateLt, setDateLt] = useState<Moment>(moment());
  const [dateGte, setDateGte] = useState<Moment>(moment().subtract(30, "day"));

  const paginate = usePagination({ defaultSort: "updatedAt" });
  const { order, sort, page, limit, body } = paginate;
  const { handleChangeLimit, handleChangePage, handleSort } = paginate;

  const history = usePavmHistory({
    ...body,
    host,
    serialNumber,
    updatedAt: {
      gte: dateGte.toISOString(),
      lt: dateLt.toISOString(),
    },
  });
  const data = history?.data?.data;

  const handleClose = () => onClose(selectedValue);

  const DateFrom = () => (
    <TextField
      id="date-filter-gte"
      size="small"
      label="From"
      type="datetime-local"
      value={dateGte?.format("YYYY-MM-DDTHH:mm")}
      onChange={(e: any) => setDateGte(moment(e.target.value))}
      InputLabelProps={{ shrink: true }}
      sx={{ mr: 2 }}
    />
  );

  const DateTo = () => (
    <TextField
      id="date-filter-lt"
      size="small"
      label="To"
      type="datetime-local"
      value={dateLt?.format("YYYY-MM-DDTHH:mm")}
      onChange={(e: any) => setDateLt(moment(e.target.value))}
      InputLabelProps={{ shrink: true }}
    />
  );

  // update 'to' date on open
  useEffect(() => {
    if (open) setDateLt(moment());
  }, [open]);

  return (
    <Dialog fullWidth maxWidth="xl" open={open} onClose={handleClose}>
      <Box sx={conf.STYLE.HISTORY}>
        <SectionHeader
          icon={<HistoryOutlined />}
          title={`${config.host.toUpperCase()} (${data?.length ?? 0})`}
          buttons={[
            <DateFrom key={"history-date-from"} />,
            <DateTo key={"history-date-to"} />,
            <TablePagination
              key={"history-pagination"}
              component="div"
              rowsPerPageOptions={[10, 25, 50, 100]}
              page={page}
              count={history?.data?.meta.total || 0}
              rowsPerPage={limit}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeLimit}
            />,
            <Refresh
              key={"history-refresh"}
              onClick={history.refetch}
              isFetching={history.isFetching}
            />,
          ]}
        />

        <Table aria-label="history" stickyHeader>
          <TableHead>
            <TableRow key={"history-table-header"}>
              {conf.COLUMNS.HISTORY.map((column) =>
                column.key ? (
                  <TableCell
                    key={column.label}
                    sortDirection={order}
                    style={{ whiteSpace: "nowrap" }}
                    align={column.isHeader ? "left" : "right"}
                  >
                    <TableSortLabel
                      active={sort === column.key}
                      direction={sort === column.key ? order : "asc"}
                      onClick={(event: MouseEvent) =>
                        handleSort(event, column.key)
                      }
                    >
                      <b>{column.label}</b>
                      {sort === column.key ? (
                        <Box component="span" sx={visuallyHidden}>
                          {order === "desc"
                            ? "sorted descending"
                            : "sorted ascending"}
                        </Box>
                      ) : null}
                    </TableSortLabel>
                  </TableCell>
                ) : (
                  <TableCell
                    key={column.label}
                    style={{ whiteSpace: "nowrap" }}
                    align={column.isHeader ? "left" : "right"}
                    sortDirection={order}
                  >
                    <b>{column.label}</b>
                  </TableCell>
                )
              )}
            </TableRow>
          </TableHead>

          {history.isLoading ? (
            <TableBody>
              <TableRow key={`${host}-history-row-loading`}>
                <TableCell colSpan={11}>
                  <LinearIndeterminate />
                </TableCell>
              </TableRow>
            </TableBody>
          ) : data?.length === 0 ? (
            <TableBody>
              <EmptyTableRow
                key={`${host}-history-row-empty`}
                type="history"
                colSpan={11}
              />
            </TableBody>
          ) : (
            <TableBody>
              {data?.map(
                ({ id, updatedAt, data: { data, error } }: HistorySchema) =>
                  !error ? (
                    <TableRow key={id} sx={conf.LAST_CHILD_NO_BORDER}>
                      <TableCell component="th" scope="row">
                        {updatedAt}
                      </TableCell>

                      <TableCell />

                      <TableCell align="right">
                        {useSmartContent(data?.resources?.cpuUsage?.data)}
                      </TableCell>

                      <TableCell align="right">
                        {useSmartContent(data?.resources?.cpuTemperature?.data)}
                      </TableCell>

                      <TableCell />

                      <TableCell align="right">
                        {useSmartContent(
                          data?.resources?.memory?.data.available
                        )}
                      </TableCell>

                      <TableCell align="right">
                        {useSmartContent(data?.resources?.memory?.data.free)}
                      </TableCell>

                      <TableCell align="right">
                        {useSmartContent(data?.resources?.memory?.data.total)}
                      </TableCell>

                      <TableCell align="right">
                        {conf.HA_ROLE?.[
                          data?.base?.role?.data as keyof typeof conf.HA_ROLE
                        ] ? (
                          <Chip
                            size="small"
                            variant="filled"
                            icon={
                              conf.HA_ROLE?.[
                                data?.base?.role
                                  ?.data as keyof typeof conf.HA_ROLE
                              ].icon
                            }
                            label={
                              conf.HA_ROLE?.[
                                data?.base?.role
                                  ?.data as keyof typeof conf.HA_ROLE
                              ].title
                            }
                            color={
                              conf.HA_ROLE?.[
                                data?.base?.role
                                  ?.data as keyof typeof conf.HA_ROLE
                              ].color
                            }
                          />
                        ) : (
                          "--"
                        )}
                      </TableCell>

                      <TableCell align="right">
                        {useSmartContent(data?.base?.clock?.data)}
                      </TableCell>

                      <TableCell align="right">
                        <JsonModal data={data} type="Raw" title={host} />
                      </TableCell>
                    </TableRow>
                  ) : (
                    <TableRow key={`${host}-${updatedAt}`}>
                      <TableCell component="th" scope="row">
                        {updatedAt}
                      </TableCell>

                      <TableCell align="right" colSpan={11}>
                        <ErrorChip
                          label={"History collection failed"}
                          info={error ?? "No data found"}
                        />
                      </TableCell>
                    </TableRow>
                  )
              )}
            </TableBody>
          )}
        </Table>
      </Box>
    </Dialog>
  );
};

export default HistoryView;
